import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import type {CheckoutProfileWrite} from '../types';
import {
  getCheckoutProfileDocumentCells,
  getCheckoutProfileDocumentCellKey,
  getCheckoutProfileDocumentTypes,
} from './getCheckoutProfileDocumentCells';

function normalizeSettings(profile: CheckoutProfileWrite): CheckoutProfileWrite {
  if (profile.typeOfSale === 'INTERNAL') {
    return {
      name: profile.name,
      branchId: profile.branchId,
      typeOfSale: profile.typeOfSale,
      isActive: profile.isActive,
      documentTypes: {correctiveInvoice: profile.documentTypes?.correctiveInvoice},
      documents: profile.documents,
    };
  }

  const methods = profile.payments?.methods.map((payment) => payment.method) ?? [];
  const depositMethods = (profile.deposit?.methods ?? []).filter((method) =>
    methods.includes(method)
  );

  return {
    ...profile,
    payments: isNotNil(profile.payments)
      ? {
          ...profile.payments,
          defaultMethod:
            isNotNil(profile.payments.defaultMethod) &&
            methods.includes(profile.payments.defaultMethod)
              ? profile.payments.defaultMethod
              : undefined,
        }
      : profile.payments,
    deposit: match(profile.deposit)
      .with(Pattern.nullish, (deposit) => deposit)
      .with({isAllowed: true}, (deposit) => ({
        ...deposit,
        methods: depositMethods,
        defaultMethod:
          isNotNil(deposit.defaultMethod) && depositMethods.includes(deposit.defaultMethod)
            ? deposit.defaultMethod
            : undefined,
      }))
      .otherwise(() => ({isAllowed: false})),
    documentTypes: {
      ...profile.documentTypes,
      correctiveTaxDocument:
        getCheckoutProfileDocumentTypes(profile).includes('TAX_DOCUMENT') &&
        Boolean(profile.documentTypes?.correctiveTaxDocument),
    },
  };
}

export function normalizeCheckoutProfileDraft(profile: CheckoutProfileWrite): CheckoutProfileWrite {
  const normalized = normalizeSettings(profile);
  const applicable = new Set(
    getCheckoutProfileDocumentCells(normalized).map(getCheckoutProfileDocumentCellKey)
  );

  return {
    ...normalized,
    documents: profile.documents.filter((row) =>
      applicable.has(getCheckoutProfileDocumentCellKey(row))
    ),
  };
}
